import React                      from 'react';
import { observer }               from 'mobx-react-lite';
import View                       from '@widesk-ui/components/View';
import Button                     from '@widesk-ui/components/Button';
import Spin                       from '@widesk-ui/components/Spin';
import Tooltip                    from '@widesk-ui/components/Tooltip';
import { FormModal }              from '@widesk-ui/components/modal/FormModal';
import PagedCollection            from '@widesk-core/models/PagedCollection';
import StonecodeFileFileModel     from '@models/file/StonecodeFileFileModel';
import FileForm                   from '@/forms/FileForm';
import { functionalityStore }     from '@/components/Functionality';
import { openLimitUpgradePrompt } from '@/components/Functionality/functionalityGuards';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore  from '@/TokamapEditor/stores/currentProjectEditorStore';
import useDesignTokens            from '@/hooks/useDesignTokens';

const SHOWN_IMAGES = 60;

/** Contenu de la fenêtre d'image : en importer une nouvelle, ou reprendre une de celles déjà envoyées sur le projet. */
export type PickedImage = {
	src: string;
	// `downloadPath` n'a pas d'extension : le format se lit sur le fichier d'origine.
	svg: boolean;
};

const MapImagePicker = observer(({ onPicked }: { onPicked: (image: PickedImage) => unknown }) => {
	const tokens = useDesignTokens();
	const [limitReached, setLimitReached] = React.useState<boolean>();
	const [images] = React.useState(() => new PagedCollection(StonecodeFileFileModel));

	const mapUrns = currentProjectEditorStore.get.mapCollection.models.filter(map => !!map.id).map(map => map.urn);

	React.useEffect(() => {
		functionalityStore.whenLimitReached({ reference: 'nb_images' }).then(setLimitReached, () => setLimitReached(true));
	}, []);

	React.useEffect(() => {
		if (!mapUrns.length) return;

		void images
			.setRequiredFilter('typeSource.source.entityUrn', mapUrns)
			.setRequiredFilter('typeSource.type.reference', 'map_image')
			.setSort('createdAt', 'desc')
			.setItemsPerPage(SHOWN_IMAGES)
			.list();
	}, [mapUrns.join()]);

	// Une même image envoyée plusieurs fois donne autant de fichiers : seul le plus récent est proposé.
	const distinctImages = images.models.filter((image, index, all) =>
		all.findIndex(other => other.name === image.name && other.size === image.size) === index);

	const pick = async (image: StonecodeFileFileModel) => {
		await onPicked({ src: image.downloadPath, svg: /\.svg$/i.test(image.name) });

		FormModal.close();
	};

	return (
		<View gap="lg">
			{limitReached === false && (
				<FileForm
					forceSvgToHaveWidthAndHeight
					typeReference="map_image"
					sourceUrn={currentEditorStore.map?.urn}
					hideDescription
					hideType
					submitOnChange
					required
					onSuccessFiles={async (files, rawFiles) => {
						functionalityStore.refreshUsagesOf('nb_images');

						const src = files[0]?.downloadPath;

						if (src) return onPicked({ src, svg: rawFiles[0]?.type === 'image/svg+xml' });
					}}
				/>
			)}

			{limitReached && (
				<View row centerV spread gap="sm" p="sm" br={8} bg={tokens.surface.subtle}>
					<View size={13} color={tokens.text.secondary}>
						La limite d'images de votre abonnement est atteinte : vous pouvez reprendre une image déjà envoyée.
					</View>

					<Button size="small" onClick={() => openLimitUpgradePrompt({ reference: 'nb_images' })}>
						Augmenter la limite
					</Button>
				</View>
			)}

			<View gap="sm">
				<View row centerV spread>
					<View bold size={14}>Images du projet</View>
					{images.total > images.length && (
						<View size={12} color={tokens.text.muted}>{`Les ${images.length} plus récentes sur ${images.total}`}</View>
					)}
				</View>

				{images.isLoading && !images.length ? (
					<View center p="md"><Spin /></View>
				) : distinctImages.length ? (
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
						{distinctImages.map(image => (
							<Tooltip key={image.id} title={image.name}>
								<button
									type="button"
									aria-label={`Utiliser ${image.name}`}
									onClick={() => pick(image)}
									style={{
										aspectRatio: '1',
										padding: 0,
										borderRadius: 8,
										overflow: 'hidden',
										cursor: 'pointer',
										border: `1px solid ${tokens.border.default}`,
										background: `repeating-conic-gradient(${tokens.border.subtle} 0 25%, transparent 0 50%) 0 0 / 12px 12px`,
									}}
								>
									{/* Adresse brute, sans CORS : voir `ImageThumbnail`. */}
									<img
										src={image.downloadPath}
										alt=""
										loading="lazy"
										style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
									/>
								</button>
							</Tooltip>
						))}
					</div>
				) : (
					<View size={13} color={tokens.text.muted}>Aucune image n'a encore été envoyée sur ce projet.</View>
				)}
			</View>
		</View>
	);
});

export default MapImagePicker;
